import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView, ImageView, View } from '@play-co/timestep-core/ui';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import DreamDriftAvatarPin from 'src/game/dreamDrift/components/DreamDriftAvatarPin';
import DreamDriftNode from 'src/game/dreamDrift/components/DreamDriftNode';
import {
  dreamDriftCostumeAssets,
  dreamDriftWearPet,
} from 'src/game/dreamDrift/dreamDrift.costumes';
import { dreamDriftTheme } from 'src/game/dreamDrift/dreamDrift.theme';
import { createUIPet } from 'src/game/pet/pet.utils';
import i18n from 'src/lib/i18n/i18n';
import { movesPinchCheck } from 'src/lib/pinch';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import uiConfig from 'src/lib/ui/config';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';
import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import { dreamDriftGetInstanceRemainingTime } from 'src/replicant/dreamDrift/dreamDrift.getters';
import { now, user } from 'src/StateObserver';

/** Horizontal position of the left progression node inside the comparison panel. */
const LEFT_NODE_X = 145;

/** Horizontal position of the right progression node inside the comparison panel. */
const RIGHT_NODE_X = 451;

/** Values required to describe one Dream Drift continue decision. */
export type PopupDreamDriftContinueInitArgs = {
  /** Dream Drift node currently being attempted. */
  currentLevel: number;
  /** Dream Drift node restored when the player declines the continue. */
  returnLevel: number;
  /** Gem price of the current shared Match-2 continue tier. */
  price: number;
  /** Player avatar displayed above the active node. */
  avatar: string;
  /** Hero selected for the current Dream Drift segment. */
  hero?: DreamDriftHero;
};

/** Close callback returning whether the player accepted the gem continue. */
type PopupDreamDriftContinueClose = (continued: boolean) => void;

/** Gem-only continue popup showing the Dream Drift progression consequence. */
export default class PopupDreamDriftContinue extends PopupBasicLazy<PopupDreamDriftContinueClose> {
  /** Dressed version of the player's pet representing the selected hero. */
  private readonly heroPet = createUIPet('normal');

  /** Copy explaining the progression consequence of declining. */
  private readonly consequenceText: LangBitmapFontTextView;

  /** Comparison node showing either the restored checkpoint or next level. */
  private readonly comparisonNode: DreamDriftNode;

  /** Arrow showing rollback or blocked-forward progression. */
  private readonly progressionArrow: ImageScaleView;

  /** Active node currently occupied by the player. */
  private readonly currentNode: DreamDriftNode;

  /** Player marker displayed above the active node. */
  private readonly avatarPin: DreamDriftAvatarPin;

  /** Main action that confirms the displayed gem purchase. */
  private readonly continueButton: ButtonScaleViewWithText;

  /** Gem price used by the current shared Match-2 continue tier. */
  private price = 0;

  /**
   * Create the Dream Drift continue composition and purchase interaction.
   *
   * @param opts Popup parent and typed close callback supplied by the registry.
   */
  constructor(opts: { superview: View; close: PopupDreamDriftContinueClose }) {
    // Reuse the standard Buruburu popup frame and title treatment from the reference.
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      width: 676,
      height: 760,
      offsetY: -25,
      darkerBg: true,
      skipMessage: true,
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_continue',
      },
    });

    // Match the red continue heading shown by other out-of-moves popups.
    this.title.title.updateOpts({ color: '#E25959' });

    // Display the selected hero beside the checkpoint warning.
    this.heroPet.updateOpts({
      superview: this.box,
      x: 145,
      y: 260,
      scale: 0.75,
    });

    // Build the speech bubble explaining the consequence of closing the popup.
    const speechBubble = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/conversation/speechbubble.png',
      imgScale: 0.66,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 44, right: 44 },
        vertical: { top: 44, bottom: 44 },
      },
      width: 370,
      height: 190,
      x: 265,
      y: 105,
    });

    // Point the warning bubble toward the dressed hero.
    new ImageView({
      superview: speechBubble,
      image: 'assets/ui/conversation/speechbubble_tail.png',
      width: 82,
      height: 67,
      x: 8,
      y: 72,
      r: Math.PI / 2,
      scale: 0.5,
    });

    // Center the dynamic progression consequence inside the warning bubble.
    this.consequenceText = new LangBitmapFontTextView({
      superview: speechBubble,
      x: 20,
      y: 0,
      width: speechBubble.style.width - 30,
      height: speechBubble.style.height,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
      font: 'Body',
      size: 34,
      color: '#985C25',
      isRichText: true,
    });

    // Group the current and restored nodes inside one highlighted progression panel.
    const progressionPanel = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/popups/panel_highlight.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 20, right: 20 },
        vertical: { top: 20, bottom: 20 },
      },
      width: this.box.style.width - 80,
      height: 235,
      x: 40,
      y: 365,
    });

    // Display the checkpoint destination on the default left side of the diagram.
    this.comparisonNode = new DreamDriftNode({
      superview: progressionPanel,
      x: LEFT_NODE_X,
      y: 150,
      scale: 0.78,
    });

    // Place the supplied arrow between the current node and restored checkpoint.
    this.progressionArrow = new ImageScaleView({
      superview: progressionPanel,
      image: 'assets/ui/dreamDrift/common/icon_arrow.png',
      width: 119,
      height: 65,
      x: progressionPanel.style.width * 0.5,
      y: 95,
      centerOnOrigin: true,
      centerAnchor: true,
    });

    // Display the active Dream Drift node on the right side of the diagram.
    this.currentNode = new DreamDriftNode({
      superview: progressionPanel,
      x: RIGHT_NODE_X,
      y: 150,
      scale: 0.78,
    });

    // Keep the player's avatar and selected hero badge attached to the active node.
    this.avatarPin = new DreamDriftAvatarPin({
      superview: progressionPanel,
      x: RIGHT_NODE_X,
      y: 125,
      scale: 0.55,
      zIndex: 2,
    });

    // Match the regular continue button's skin, sizing, and trailing gem icon.
    this.continueButton = new ButtonScaleViewWithText({
      ...uiConfig.buruburu.care.confirmation.okButton,
      superview: this.box,
      width: 477,
      height: 110,
      x: this.box.style.width * 0.5,
      y: 675,
      centerOnOrigin: true,
      fontSize: 50,
      icon: {
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 54,
        height: 45,
        offset: { x: 190, y: 0 },
      },
      labelOffsetX: -35,
      localeText: () => `${i18n('dreamDrift.continue.playOn')}  ${this.price}`,
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_continue_purchase',
      },
      onClick: async () => {
        // Prevent duplicate purchase attempts while the gem pinch flow is active.
        this.continueButton.updateOpts({ canHandleEvents: false });
        sounds.sfx.play('ok');

        // Decline an expired attempt before offering or purchasing extra moves.
        if (dreamDriftGetInstanceRemainingTime(user(), now()) === 0) {
          opts.close(false);
          return;
        }

        // Reuse the standard continue balance check and gem-shop fallback.
        const canContinue = await movesPinchCheck(
          this.price,
          'match2_shop_moves',
        );

        // Close successfully only after the shared balance check passes.
        if (canContinue) {
          opts.close(true);
          return;
        }

        // Restore the action when the player returns without enough gems.
        this.continueButton.updateOpts({ canHandleEvents: true });
      },
    });
  }

  /**
   * Load the existing popup, hero, node, pin, arrow, and currency artwork.
   *
   * @returns Asset group required before the popup opens.
   */
  protected override getAssetsGroup(): AssetGroup {
    // Load the shared Dream Drift artwork alongside this popup's external assets.
    return new AssetGroup([
      ...dreamDriftCostumeAssets,
      dreamDriftTheme().getNodeImage({ status: 'open', isCheckpoint: false }),
      ...AssetGroup.constructURLs('assets/ui/dreamDrift/common/'),
      'assets/ui/conversation/speechbubble.png',
      'assets/ui/conversation/speechbubble_tail.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/popups/panel_highlight.png',
      GENERIC_AVATAR,
    ]);
  }

  /**
   * Configure the current node, comparison node, selected hero, and gem price.
   *
   * @param args Dream Drift progression and shared continue price.
   * @returns Nothing.
   */
  public override init(args: PopupDreamDriftContinueInitArgs): void {
    // Retain the current gem price consumed by the purchase action.
    this.price = args.price;

    // Checkpoint losses stay on their current node instead of rolling backward.
    const staysOnCurrentLevel = args.currentLevel === args.returnLevel;
    const comparisonLevel = staysOnCurrentLevel
      ? args.currentLevel + 1
      : args.returnLevel;

    // Describe either blocked advancement or the normal checkpoint rollback.
    this.consequenceText.localeText = () =>
      i18n(
        staysOnCurrentLevel
          ? 'dreamDrift.continue.willNotAdvance'
          : 'dreamDrift.continue.returnToLevel',
        { level: comparisonLevel },
      );

    // Draw both progression values with the existing upcoming-node artwork.
    this.comparisonNode.setup({
      index: comparisonLevel - 1,
      isCheckpoint: false,
      status: 'open',
    });
    this.currentNode.setup({
      index: args.currentLevel - 1,
      isCheckpoint: false,
      status: 'open',
    });

    // Keep retained checkpoints pinned left while normal setbacks pin the right node.
    const currentNodeX = staysOnCurrentLevel ? LEFT_NODE_X : RIGHT_NODE_X;
    const comparisonNodeX = staysOnCurrentLevel ? RIGHT_NODE_X : LEFT_NODE_X;
    this.currentNode.updateOpts({ x: currentNodeX });
    this.comparisonNode.updateOpts({ x: comparisonNodeX });
    this.avatarPin.updateOpts({ x: currentNodeX });

    // Point toward the next locked level when the player remains at a checkpoint.
    this.progressionArrow.updateOpts({ flipX: staysOnCurrentLevel });

    // Dress the popup pet when the active selection is still supported.
    if (args.hero) dreamDriftWearPet(this.heroPet, args.hero);

    // Display the supplied player identity on the current node.
    this.avatarPin.setup({
      avatar: args.avatar || GENERIC_AVATAR,
      hero: args.hero,
    });

    // Refresh every dynamic text field before opening the popup.
    this.consequenceText.notifyLocaleTextChanged();
    this.continueButton.label.notifyLocaleTextChanged();

    // Open with the localized continue title after all state is configured.
    super.init({ title: i18n('dreamDrift.continue.title') });
  }
}
